/**
 * TicketsView — the live ticketing surface.
 *
 * Trent, Grant, and our Claude agents create / take / work tickets here.
 * Each ticket carries a spec, owner, priority, time estimate, deadline,
 * and an append-only timeline. The board is live (subscribeTickets), the
 * detail drawer drives status / owner / comment mutations, and the new
 * ticket form mints sequential TIQ-### ids.
 *
 * Wired to the tickets adapter (src/lib/db/tickets.ts) which runs against
 * localStorage in dev and Firestore in prod — same code path either way.
 * Magenta-only accent per brand rule; Lucide icons only.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus,
  X,
  Inbox,
  Clock,
  CalendarClock,
  User as UserIcon,
  MessageSquare,
  Send,
  CircleDot,
  CheckCircle2,
  Ban,
  Eye,
  CircleDashed,
  Hand,
  Loader2,
  AlertTriangle,
  Bot,
  Sparkles,
  ShieldCheck,
  Zap,
  ListChecks,
  Map as MapIcon,
  type LucideIcon,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { LoadingState, EmptyState } from '../components/States';
import { C } from '../lib/tokens';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  TICKET_OWNERS,
  addTicketComment,
  bootstrapTicketsIfEmpty,
  claimTicket,
  createTicket,
  setTicketStatus,
  subscribeTickets,
  updateTicket,
} from '../lib/db/tickets';
import { subscribeTasks } from '../lib/db/tasks';
import { subscribeRoadmap } from '../lib/db/roadmap';
import { milestonesLinkingTicket } from '../lib/crm/milestones';
import type {
  ProjectTask,
  RoadmapItem,
  Ticket,
  TicketPriority,
  TicketStatus,
} from '../lib/db/types';
import {
  AUTOPILOT_AGENTS,
  isAgentOwner,
  useTicketAutopilot,
  type AutopilotEvent,
} from '../lib/agent/ticketAutopilot';

// --- Status / priority presentation ----------------------------------------

const STATUS_ORDER: TicketStatus[] = [
  'backlog',
  'open',
  'claimed',
  'in-progress',
  'blocked',
  'review',
  'done',
  'cancelled',
];

const STATUS_META: Record<
  TicketStatus,
  { label: string; pill: string; icon: LucideIcon }
> = {
  backlog: { label: 'Backlog', pill: 'glass-pill', icon: CircleDashed },
  open: { label: 'Open', pill: 'glass-pill glass-pill--blue', icon: CircleDot },
  claimed: { label: 'Claimed', pill: 'glass-pill glass-pill--blue', icon: Hand },
  'in-progress': {
    label: 'In progress',
    pill: 'glass-pill glass-pill--magenta',
    icon: Loader2,
  },
  blocked: { label: 'Blocked', pill: 'glass-pill glass-pill--orange', icon: Ban },
  review: { label: 'Review', pill: 'glass-pill glass-pill--orange', icon: Eye },
  done: { label: 'Done', pill: 'glass-pill glass-pill--green', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', pill: 'glass-pill', icon: X },
};

const PRIORITY_META: Record<TicketPriority, { label: string; pill: string }> = {
  p0: { label: 'P0', pill: 'glass-pill glass-pill--orange' },
  p1: { label: 'P1', pill: 'glass-pill glass-pill--magenta' },
  p2: { label: 'P2', pill: 'glass-pill glass-pill--blue' },
  p3: { label: 'P3', pill: 'glass-pill' },
};

const PRIORITIES: TicketPriority[] = ['p0', 'p1', 'p2', 'p3'];

function isOverdue(t: Ticket): boolean {
  if (!t.deadline) return false;
  if (t.status === 'done' || t.status === 'cancelled') return false;
  const d = new Date(`${t.deadline}T23:59:59`).getTime();
  return Number.isFinite(d) && d < Date.now();
}

function fmtRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 14) return `${days}d ago`;
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ===========================================================================

/** Ticket to open on arrival (from the ⌘K command palette). The nonce
 *  lets the same ticket be re-focused twice in a row. */
export interface TicketFocus {
  id: string;
  nonce: number;
}

interface TicketsViewProps {
  focus?: TicketFocus | null;
}

export function TicketsView({ focus = null }: TicketsViewProps) {
  const { user } = useAuth();
  const canMutate = user?.role !== 'viewer';
  const actor = user?.displayName?.split(' ')[0] ?? 'You';

  const [tickets, setTickets] = useState<Ticket[]>([]);
  // First-snapshot flag: distinguishes "still loading" from "loaded, empty"
  // so we can show a skeleton instead of a premature empty state.
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState<TicketStatus | 'all' | 'active'>('active');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const autopilot = useTicketAutopilot();
  const lastFocusNonce = useRef(0);

  useEffect(() => {
    let active = true;
    bootstrapTicketsIfEmpty().catch(() => undefined);
    const unsub = subscribeTickets((records) => {
      if (active) {
        // Newest activity first.
        setTickets(
          [...records].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)),
        );
        setLoaded(true);
      }
    });
    return () => {
      active = false;
      unsub();
    };
  }, []);

  // Entity quick-jump: open the ticket chosen in the ⌘K palette. Widen the
  // filter to 'all' so the row is guaranteed to appear in the list even if
  // it's done/cancelled and the active filter would otherwise hide it.
  useEffect(() => {
    if (!focus || focus.nonce === lastFocusNonce.current) return;
    lastFocusNonce.current = focus.nonce;
    setSelectedId(focus.id);
    setFilter('all');
  }, [focus]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: tickets.length, active: 0 };
    for (const s of STATUS_ORDER) c[s] = 0;
    for (const t of tickets) {
      c[t.status] = (c[t.status] ?? 0) + 1;
      if (t.status !== 'done' && t.status !== 'cancelled') c.active += 1;
    }
    return c;
  }, [tickets]);

  const visible = useMemo(() => {
    if (filter === 'all') return tickets;
    if (filter === 'active')
      return tickets.filter(
        (t) => t.status !== 'done' && t.status !== 'cancelled',
      );
    return tickets.filter((t) => t.status === filter);
  }, [tickets, filter]);

  const selected = useMemo(
    () => tickets.find((t) => t.id === selectedId) ?? null,
    [tickets, selectedId],
  );

  const filterPills: { key: TicketStatus | 'all' | 'active'; label: string }[] = [
    { key: 'active', label: 'Active' },
    { key: 'all', label: 'All' },
    ...STATUS_ORDER.map((s) => ({ key: s, label: STATUS_META[s].label })),
  ];

  return (
    <div className="app-container" style={{ display: 'grid', gap: 18 }}>
      {/* Header */}
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div>
          <span className="t-eyebrow">Tickets</span>
          <h1 className="t-page-title" style={{ margin: '6px 0 0' }}>
            Work queue
          </h1>
          <p style={{ margin: '4px 0 0', color: C.textMuted }} className="t-body">
            Specs, owners, estimates, and a running timeline — worked by the
            team and our Claude agents.
          </p>
        </div>
        {canMutate && (
          <button
            className="glass-btn glass-btn--primary"
            onClick={() => setShowForm(true)}
          >
            <Plus size={13} /> New ticket
          </button>
        )}
      </header>

      {/* Agent autopilot */}
      {canMutate && <AutopilotPanel tickets={tickets} autopilot={autopilot} />}

      {/* Filter pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {filterPills.map((p) => {
          const n = counts[p.key] ?? 0;
          const isActive = filter === p.key;
          return (
            <button
              key={p.key}
              type="button"
              className="glass-btn"
              data-active={isActive}
              onClick={() => setFilter(p.key)}
              style={{
                opacity: isActive ? 1 : 0.72,
                borderColor: isActive ? C.magenta : undefined,
              }}
            >
              {p.label}
              <span
                className="t-meta"
                style={{ marginLeft: 4, color: C.textMuted }}
              >
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {/* Board */}
      <GlassPanel variant="default">
        {!loaded ? (
          <LoadingState rows={5} label="Loading tickets" />
        ) : visible.length === 0 ? (
          <EmptyState
            icon={<Inbox size={22} strokeWidth={1.75} />}
            title="No tickets in this view."
          />
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {visible.map((t) => (
              <TicketRow
                key={t.id}
                ticket={t}
                onOpen={() => setSelectedId(t.id)}
              />
            ))}
          </div>
        )}
      </GlassPanel>

      {/* Detail drawer */}
      {selected && (
        <TicketDetail
          ticket={selected}
          canMutate={canMutate}
          actor={actor}
          onClose={() => setSelectedId(null)}
        />
      )}

      {/* New ticket form */}
      {showForm && (
        <TicketForm
          createdBy={actor}
          onClose={() => setShowForm(false)}
          onCreated={(id) => {
            setShowForm(false);
            setSelectedId(id);
            setFilter('active');
          }}
        />
      )}
    </div>
  );
}

// --- Agent autopilot --------------------------------------------------------

function AutopilotPanel({
  tickets,
  autopilot,
}: {
  tickets: Ticket[];
  autopilot: ReturnType<typeof useTicketAutopilot>;
}) {
  const { enabled, toggle, mode, setMode, lastEvent, recentEvents, busy, workerConfigured } =
    autopilot;
  const governed = mode === 'propose';

  // Per-agent live load (claimed + in-progress) so the roster shows who's busy.
  const load = useMemo(() => {
    const m: Record<string, number> = {};
    for (const a of AUTOPILOT_AGENTS) m[a.id] = 0;
    for (const t of tickets) {
      if (
        isAgentOwner(t.owner) &&
        (t.status === 'claimed' || t.status === 'in-progress')
      ) {
        m[t.owner as string] = (m[t.owner as string] ?? 0) + 1;
      }
    }
    return m;
  }, [tickets]);

  const queued = useMemo(
    () =>
      tickets.filter(
        (t) => (t.status === 'open' || t.status === 'backlog') && !t.owner,
      ).length,
    [tickets],
  );

  return (
    <GlassPanel
      variant={enabled ? 'magenta' : 'subtle'}
      animate={false}
      style={{ padding: '14px 16px' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            width: 38,
            height: 38,
            borderRadius: 11,
            display: 'inline-grid',
            placeItems: 'center',
            flex: '0 0 auto',
            background: enabled
              ? 'linear-gradient(135deg, rgba(255,0,255,0.28), rgba(187,65,152,0.16))'
              : 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.14)',
            color: enabled ? '#fff' : C.textMuted,
          }}
        >
          <Bot size={20} strokeWidth={1.75} />
        </span>

        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="t-card-title">Agent autopilot</span>
            {enabled && (
              <span
                className="glass-pill glass-pill--magenta"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: C.magenta,
                    animation: 'pulse 1.6s ease-in-out infinite',
                  }}
                />
                Live
              </span>
            )}
            {/* Which engine is doing the work — real Claude worker vs simulation. */}
            <span
              className={workerConfigured ? 'glass-pill glass-pill--green' : 'glass-pill'}
              title={
                workerConfigured
                  ? 'Agent work is routed through your hosted Claude worker.'
                  : 'Simulation mode — set VITE_AGENT_API_URL to connect the real Claude worker.'
              }
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
            >
              {workerConfigured ? (
                <Sparkles size={11} />
              ) : (
                <CircleDashed size={11} />
              )}
              {workerConfigured ? 'Connected to Claude worker' : 'Simulation mode'}
            </span>
          </div>
          <p
            className="t-meta"
            style={{ margin: '2px 0 0', color: C.textMuted }}
          >
            {enabled
              ? lastEvent
                ? lastEvent.text
                : busy
                  ? 'Scanning the queue…'
                  : queued > 0
                    ? governed
                      ? `${queued} ticket${queued === 1 ? '' : 's'} waiting — proposing assignments for your approval.`
                      : `${queued} ticket${queued === 1 ? '' : 's'} waiting to be picked up.`
                    : 'Queue clear — watching for new work.'
              : governed
                ? 'Agents propose which tickets to take; you approve in the inbox, then they work them through review.'
                : 'Let Claude agents claim open tickets and work them through review — no approval needed.'}
          </p>
        </div>

        {/* Agent roster with live load */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {AUTOPILOT_AGENTS.map((a) => {
            const n = load[a.id] ?? 0;
            const active = enabled && n > 0;
            return (
              <span
                key={a.id}
                title={`${a.label} — ${a.blurb}`}
                className="glass-pill"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  borderColor: active ? 'rgba(255,0,255,0.45)' : undefined,
                  opacity: active ? 1 : 0.7,
                }}
              >
                <Sparkles size={11} />
                {a.label}
                <span className="t-meta" style={{ color: C.textMuted }}>
                  {n}
                </span>
              </span>
            );
          })}
        </div>

        {/* Governance mode — whether agents propose (human-approved) or act
            autonomously. Defaults to governed so there are no silent writes. */}
        <div
          role="group"
          aria-label="Autopilot governance mode"
          title={
            governed
              ? 'Governed: agents queue assignments to the approvals inbox for you to approve.'
              : 'Autonomous: agents claim and work tickets with no approval step.'
          }
          style={{
            display: 'inline-flex',
            flex: '0 0 auto',
            padding: 3,
            gap: 3,
            borderRadius: 10,
            background: 'rgba(0,0,0,0.22)',
            border: '1px solid rgba(255,255,255,0.10)',
          }}
        >
          <button
            type="button"
            onClick={() => setMode('propose')}
            aria-pressed={governed}
            className="glass-btn"
            style={{
              padding: '5px 10px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              border: 'none',
              background: governed
                ? 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.14))'
                : 'transparent',
              color: governed ? '#fff' : C.textMuted,
            }}
          >
            <ShieldCheck size={13} /> Governed
          </button>
          <button
            type="button"
            onClick={() => setMode('auto')}
            aria-pressed={!governed}
            className="glass-btn"
            style={{
              padding: '5px 10px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              border: 'none',
              background: !governed
                ? 'linear-gradient(135deg, rgba(253,173,1,0.24), rgba(253,173,1,0.10))'
                : 'transparent',
              color: !governed ? '#fff' : C.textMuted,
            }}
          >
            <Zap size={13} /> Autonomous
          </button>
        </div>

        <button
          type="button"
          className={enabled ? 'glass-btn' : 'glass-btn glass-btn--primary'}
          onClick={toggle}
          style={{ flex: '0 0 auto' }}
        >
          {enabled ? 'Pause autopilot' : 'Start autopilot'}
        </button>
      </div>

      {/* Live activity ticker — a rolling feed of what the agents just did,
          so you can watch Claude take and work tickets in real time. */}
      {enabled && recentEvents.length > 0 && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {recentEvents.map((ev, i) => (
            <div
              key={`${ev.ticketId}-${ev.kind}-${i}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                opacity: i === 0 ? 1 : Math.max(0.4, 1 - i * 0.12),
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: TICKER_DOT[ev.kind],
                  flex: '0 0 auto',
                }}
              />
              <span className="t-meta" style={{ color: C.textMuted }}>
                {ev.text}
              </span>
            </div>
          ))}
        </div>
      )}
    </GlassPanel>
  );
}

/** Dot colour per autopilot event kind, for the live ticker. */
const TICKER_DOT: Record<AutopilotEvent['kind'], string> = {
  propose: C.orange,
  claim: C.magenta,
  start: C.blueElectric,
  progress: C.orangeWarm,
  review: C.blueElectric,
  done: C.green,
};

// --- Row --------------------------------------------------------------------

function TicketRow({
  ticket,
  onOpen,
}: {
  ticket: Ticket;
  onOpen: () => void;
}) {
  const sMeta = STATUS_META[ticket.status];
  const pMeta = PRIORITY_META[ticket.priority];
  const overdue = isOverdue(ticket);
  const StatusIcon = sMeta.icon;

  return (
    <button
      type="button"
      className="glass-row"
      onClick={onOpen}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center',
        gap: 14,
        textAlign: 'left',
        width: '100%',
      }}
    >
      <span className="t-meta" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {ticket.id}
      </span>

      <span style={{ minWidth: 0 }}>
        <span
          className="t-card-title"
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {ticket.title}
        </span>
        <span
          className="t-meta"
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginTop: 2,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <UserIcon size={11} /> {ticket.owner ?? 'Unassigned'}
          </span>
          {ticket.estimateHours != null && (
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Clock size={11} /> {ticket.estimateHours}h
            </span>
          )}
          {ticket.deadline && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                color: overdue ? C.orangeWarm : undefined,
                fontWeight: overdue ? 700 : undefined,
              }}
            >
              {overdue ? <AlertTriangle size={11} /> : <CalendarClock size={11} />}
              {fmtDate(ticket.deadline)}
            </span>
          )}
          <span>· updated {fmtRelative(ticket.updatedAt)}</span>
        </span>
      </span>

      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          justifySelf: 'end',
        }}
      >
        <span className={pMeta.pill}>{pMeta.label}</span>
        <span
          className={sMeta.pill}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <StatusIcon size={11} /> {sMeta.label}
        </span>
      </span>
    </button>
  );
}

// --- Detail drawer ----------------------------------------------------------

function TicketDetail({
  ticket,
  canMutate,
  actor,
  onClose,
}: {
  ticket: Ticket;
  canMutate: boolean;
  actor: string;
  onClose: () => void;
}) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const sMeta = STATUS_META[ticket.status];
  const overdue = isOverdue(ticket);

  // Escape closes the drawer — house pattern (see AccountDetailModal),
  // so keyboard users aren't stranded inside a fixed overlay.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Tasks + roadmap, subscribed only while the drawer is open — they
  // surface the ticket's PM-side handles (tasks tracking it) and the
  // milestones rolling it up, completing the task↔ticket↔roadmap chain.
  const [allTasks, setAllTasks] = useState<ProjectTask[]>([]);
  const [roadmap, setRoadmap] = useState<RoadmapItem[]>([]);
  useEffect(() => {
    const unsubs = [subscribeTasks(setAllTasks), subscribeRoadmap(setRoadmap)];
    return () => unsubs.forEach((u) => u());
  }, []);
  const linkedTasks = useMemo(
    () => allTasks.filter((t) => t.ticketId === ticket.id),
    [allTasks, ticket.id],
  );
  const linkedMilestones = useMemo(
    () => milestonesLinkingTicket(roadmap, ticket.id),
    [roadmap, ticket.id],
  );

  const onStatus = async (status: TicketStatus) => {
    if (status === ticket.status) return;
    setBusy(true);
    try {
      await setTicketStatus(ticket.id, status, actor);
    } finally {
      setBusy(false);
    }
  };

  const onOwner = async (owner: string) => {
    setBusy(true);
    try {
      if (owner === '') {
        await updateTicket(ticket.id, { owner: null }, actor);
      } else {
        await claimTicket(ticket.id, owner, actor);
      }
    } finally {
      setBusy(false);
    }
  };

  const onComment = async () => {
    const body = comment.trim();
    if (!body) return;
    setBusy(true);
    try {
      await addTicketComment(ticket.id, actor, body);
      setComment('');
    } finally {
      setBusy(false);
    }
  };

  const timeline = [...ticket.timeline].reverse();

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        background: 'rgba(6, 6, 12, 0.55)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 100%)',
          height: '100%',
          overflowY: 'auto',
          padding: 18,
        }}
      >
        <GlassPanel variant="sharp" animate={false} style={{ padding: '24px 26px' }}>
          {/* Head */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <span className="t-eyebrow">{ticket.id}</span>
              <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                {ticket.title}
              </h2>
            </div>
            <button
              type="button"
              className="glass-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>

          {/* Badges */}
          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}
          >
            <span className={PRIORITY_META[ticket.priority].pill}>
              {PRIORITY_META[ticket.priority].label}
            </span>
            <span className={sMeta.pill}>{sMeta.label}</span>
            {ticket.labels.map((l) => (
              <span key={l} className="glass-pill">
                {l}
              </span>
            ))}
          </div>

          {/* Spec */}
          <div style={{ marginTop: 16 }}>
            <span className="t-eyebrow">Spec</span>
            <p
              className="t-body"
              style={{
                margin: '6px 0 0',
                color: C.textMuted,
                whiteSpace: 'pre-wrap',
              }}
            >
              {ticket.spec || '— no spec —'}
            </p>
          </div>

          {/* Meta grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
              marginTop: 16,
            }}
          >
            <Meta label="Owner" value={ticket.owner ?? 'Unassigned'} />
            <Meta label="Created by" value={ticket.createdBy} />
            <Meta
              label="Estimate"
              value={ticket.estimateHours != null ? `${ticket.estimateHours}h` : '—'}
            />
            <Meta
              label="Deadline"
              value={ticket.deadline ? fmtDate(ticket.deadline) : '—'}
              warn={overdue}
            />
            <Meta label="Created" value={fmtRelative(ticket.createdAt)} />
            <Meta label="Updated" value={fmtRelative(ticket.updatedAt)} />
          </div>

          {/* Linked work — read-only here; tasks link from the Tasks
              board, milestones from Development → "Link work". */}
          {(linkedTasks.length > 0 || linkedMilestones.length > 0) && (
            <div style={{ marginTop: 16, display: 'grid', gap: 6 }}>
              <span className="t-eyebrow">Linked work</span>
              {linkedTasks.map((t) => (
                <span
                  key={t.id}
                  className="t-meta"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.textMuted }}
                  title={`Task — ${t.status}`}
                >
                  <ListChecks size={12} />
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textDecoration: t.status === 'done' ? 'line-through' : undefined,
                    }}
                  >
                    {t.title}
                  </span>
                  <span className="glass-pill" style={{ flex: '0 0 auto' }}>
                    {t.status}
                  </span>
                </span>
              ))}
              {linkedMilestones.map((m) => (
                <span
                  key={m.id}
                  className="t-meta"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.textMuted }}
                  title="A roadmap milestone rolls this ticket up — closing it moves the milestone's auto progress."
                >
                  <MapIcon size={12} /> Roadmap: {m.title}
                </span>
              ))}
            </div>
          )}

          {/* Controls */}
          {canMutate && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
                marginTop: 16,
              }}
            >
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Status</span>
                <select
                  className="glass-input"
                  value={ticket.status}
                  disabled={busy}
                  onChange={(e) => onStatus(e.target.value as TicketStatus)}
                >
                  {STATUS_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_META[s].label}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Owner</span>
                <select
                  className="glass-input"
                  value={ticket.owner ?? ''}
                  disabled={busy}
                  onChange={(e) => onOwner(e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {TICKET_OWNERS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {/* Timeline */}
          <div style={{ marginTop: 20 }}>
            <span className="t-eyebrow">Timeline</span>
            <ol
              style={{
                listStyle: 'none',
                margin: '8px 0 0',
                padding: 0,
                display: 'grid',
                gap: 10,
              }}
            >
              {timeline.map((e) => (
                <li
                  key={e.id}
                  style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: 99,
                      marginTop: 6,
                      flex: '0 0 auto',
                      background: C.magenta,
                    }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span className="t-body" style={{ color: C.text }}>
                      {e.text}
                    </span>
                    <span className="t-meta" style={{ display: 'block' }}>
                      {fmtRelative(e.at)} · {e.actor}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {/* Comments */}
          <div style={{ marginTop: 20 }}>
            <span
              className="t-eyebrow"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <MessageSquare size={12} /> Comments
            </span>
            <div style={{ display: 'grid', gap: 10, margin: '8px 0 0' }}>
              {ticket.comments.length === 0 && (
                <span className="t-meta">No comments yet.</span>
              )}
              {ticket.comments.map((c) => (
                <div key={c.id}>
                  <span className="t-body" style={{ color: C.text }}>
                    {c.body}
                  </span>
                  <span className="t-meta" style={{ display: 'block' }}>
                    {c.author} · {fmtRelative(c.at)}
                  </span>
                </div>
              ))}
            </div>
            {canMutate && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <input
                  className="glass-input"
                  style={{ flex: 1 }}
                  placeholder="Add a comment…"
                  value={comment}
                  disabled={busy}
                  onChange={(e) => setComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onComment();
                    }
                  }}
                />
                <button
                  type="button"
                  className="glass-btn glass-btn--primary"
                  onClick={onComment}
                  disabled={busy || !comment.trim()}
                >
                  <Send size={13} />
                </button>
              </div>
            )}
          </div>
        </GlassPanel>
      </div>
    </div>
  );
}

function Meta({
  label,
  value,
  warn,
}: {
  label: string;
  value: string;
  warn?: boolean;
}) {
  return (
    <div>
      <span className="t-eyebrow">{label}</span>
      <span
        className="t-body"
        style={{
          display: 'block',
          marginTop: 2,
          color: warn ? C.orangeWarm : C.text,
          fontWeight: warn ? 700 : undefined,
        }}
      >
        {value}
      </span>
    </div>
  );
}

// --- New ticket form --------------------------------------------------------

function TicketForm({
  createdBy,
  onClose,
  onCreated,
}: {
  createdBy: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [spec, setSpec] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('p2');
  const [owner, setOwner] = useState('');
  const [estimate, setEstimate] = useState('');
  const [deadline, setDeadline] = useState('');
  const [labels, setLabels] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * Closing is refused mid-create so a slow write can't be abandoned into a
   * half-state. WHY a single guard: Escape was already gated on `busy`, but
   * the backdrop click, header X, and Cancel button all called `onClose`
   * unguarded — the same invariant was trivially bypassed by mouse. Every
   * close path now funnels through this one gate.
   */
  const safeClose = () => {
    if (!busy) onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') safeClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const submit = async () => {
    const t = title.trim();
    if (!t) return;
    setBusy(true);
    try {
      const created = await createTicket({
        title: t,
        spec: spec.trim(),
        priority,
        owner: owner || null,
        createdBy,
        estimateHours: estimate ? Number(estimate) : null,
        deadline: deadline || null,
        labels: labels
          .split(',')
          .map((l) => l.trim())
          .filter(Boolean),
      });
      onCreated(created.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={safeClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        background: 'rgba(6, 6, 12, 0.55)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 24,
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(560px, 100%)', marginTop: 24 }}
      >
        <GlassPanel variant="sharp" animate={false} style={{ padding: '24px 26px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h2 className="t-section-title" style={{ margin: 0 }}>
              New ticket
            </h2>
            <button
              type="button"
              className="glass-btn"
              onClick={safeClose}
              disabled={busy}
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>

          <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
            <label style={{ display: 'grid', gap: 4 }}>
              <span className="t-eyebrow">Title</span>
              <input
                className="glass-input"
                placeholder="Short, action-oriented title"
                value={title}
                autoFocus
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>

            <label style={{ display: 'grid', gap: 4 }}>
              <span className="t-eyebrow">Spec</span>
              <textarea
                className="glass-input"
                rows={5}
                placeholder="What needs doing, acceptance criteria, context…"
                value={spec}
                onChange={(e) => setSpec(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </label>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
              }}
            >
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Priority</span>
                <select
                  className="glass-input"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as TicketPriority)}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_META[p].label}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Owner</span>
                <select
                  className="glass-input"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {TICKET_OWNERS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Estimate (hrs)</span>
                <input
                  className="glass-input"
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="e.g. 4"
                  value={estimate}
                  onChange={(e) => setEstimate(e.target.value)}
                />
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Deadline</span>
                <input
                  className="glass-input"
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                />
              </label>
            </div>

            <label style={{ display: 'grid', gap: 4 }}>
              <span className="t-eyebrow">Labels</span>
              <input
                className="glass-input"
                placeholder="comma,separated,labels"
                value={labels}
                onChange={(e) => setLabels(e.target.value)}
              />
            </label>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 18,
            }}
          >
            <button
              type="button"
              className="glass-btn"
              onClick={safeClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="glass-btn glass-btn--primary"
              onClick={submit}
              disabled={busy || !title.trim()}
            >
              <Plus size={13} /> Create ticket
            </button>
          </div>
        </GlassPanel>
      </div>
    </div>
  );
}
